/**
 * Timezone utilities for consistent date handling across dashboards
 * 
 * Business timezone: America/New_York (Orlando, FL - Divus headquarters)
 * These functions ensure that date ranges respect the office timezone
 * while converting properly to UTC for database queries.
 */

import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { OFFICE_TIMEZONE } from '@/locales';

/**
 * Gets today's date in office timezone (Orlando)
 */
export function getTodayInOfficeTimezone(): Date {
  return toZonedTime(new Date(), OFFICE_TIMEZONE);
}

/**
 * Formats a date to YYYY-MM-DD in office timezone (Orlando)
 */
export function formatDateInOfficeTimezone(date: Date): string {
  return formatInTimeZone(date, OFFICE_TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Gets today's date as YYYY-MM-DD string in office timezone (Orlando)
 */
export function getTodayStringInOfficeTimezone(): string {
  return formatInTimeZone(new Date(), OFFICE_TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Gets the start of a day in the user's local timezone, returned as UTC
 * Example: For 2025-11-15 in GMT-3, returns 2025-11-15T03:00:00.000Z
 */
export function getLocalDayStart(date: Date): Date {
  const localDate = new Date(date);
  localDate.setHours(0, 0, 0, 0);
  return localDate;
}

/**
 * Gets the end of a day in the user's local timezone, returned as UTC
 * Example: For 2025-11-15 in GMT-3, returns 2025-11-16T02:59:59.999Z
 */
export function getLocalDayEnd(date: Date): Date {
  const localDate = new Date(date);
  localDate.setHours(23, 59, 59, 999);
  return localDate;
}

/**
 * Gets the start of the week (Sunday) in the user's local timezone
 */
export function getLocalWeekStart(date: Date): Date {
  const localDate = new Date(date);
  localDate.setDate(localDate.getDate() - localDate.getDay());
  localDate.setHours(0, 0, 0, 0);
  return localDate;
}

/**
 * Gets the start of the month in the user's local timezone
 */
export function getLocalMonthStart(date: Date): Date {
  const localDate = new Date(date);
  localDate.setDate(1);
  localDate.setHours(0, 0, 0, 0);
  return localDate;
}

/**
 * Gets the end of the month in the user's local timezone
 */
export function getLocalMonthEnd(date: Date): Date {
  const year = date.getFullYear();
  const month = date.getMonth();
  // Last day of month at 23:59:59.999
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

/**
 * Gets a date N days ago at start of day in local timezone
 */
export function getDaysAgo(days: number): Date {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return getLocalDayStart(date);
}

/**
 * Safely parses a date string (YYYY-MM-DD) to avoid timezone issues.
 * When parsing 'YYYY-MM-DD', JavaScript treats it as UTC midnight,
 * which becomes the previous day in negative timezones (e.g., São Paulo UTC-3).
 * This function ensures the date is parsed as local midnight.
 */
export function parseDateSafe(date: Date | string | number | null | undefined): Date | null {
  if (!date) return null;
  
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }
  
  if (typeof date === 'number') {
    const d = new Date(date);
    return isNaN(d.getTime()) ? null : d;
  }
  
  // String handling - check if it's YYYY-MM-DD format
  if (typeof date === 'string') {
    // Match YYYY-MM-DD format (date only, no time)
    const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      // Parse as local date to avoid timezone issues
      const [, year, month, day] = dateOnlyMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    
    // For other formats, use standard parsing
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  return null;
}

/**
 * Checks if a payment is overdue (due date is before today in OFFICE timezone - Orlando)
 * Business logic for overdue uses office timezone, not user's local timezone
 * @param dueDate - The due date to check
 * @returns true if the payment is overdue
 */
export function isPaymentOverdue(dueDate: Date | string | null | undefined): boolean {
  const parsed = parseDateSafe(dueDate);
  if (!parsed) return false;
  
  // Get today's date string in Orlando timezone
  const todayInOrlando = getTodayStringInOfficeTimezone(); // "2025-12-30"
  
  // Get due date as YYYY-MM-DD string (already local date from parseDateSafe)
  const dueYear = parsed.getFullYear();
  const dueMonth = String(parsed.getMonth() + 1).padStart(2, '0');
  const dueDay = String(parsed.getDate()).padStart(2, '0');
  const dueString = `${dueYear}-${dueMonth}-${dueDay}`;
  
  // Compare strings: due date is overdue if it's before today in Orlando
  return dueString < todayInOrlando;
}

/**
 * Gets the number of days a payment is overdue (using OFFICE timezone - Orlando)
 * Business logic for overdue uses office timezone, not user's local timezone
 * @param dueDate - The due date to check
 * @returns Number of days overdue (0 if not overdue or invalid date)
 */
export function getDaysOverdue(dueDate: Date | string | null | undefined): number {
  const parsed = parseDateSafe(dueDate);
  if (!parsed) return 0;
  
  // Get today's date in Orlando timezone
  const todayInOrlando = getTodayStringInOfficeTimezone(); // "2025-12-30"
  const [tyear, tmonth, tday] = todayInOrlando.split('-').map(Number);
  
  // Get due date components (already local date from parseDateSafe)
  const dyear = parsed.getFullYear();
  const dmonth = parsed.getMonth() + 1;
  const dday = parsed.getDate();
  
  // Create comparable dates (both at midnight, just for day diff calculation)
  const todayDate = new Date(tyear, tmonth - 1, tday);
  const dueDateObj = new Date(dyear, dmonth - 1, dday);
  
  const diffMs = todayDate.getTime() - dueDateObj.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  return days > 0 ? days : 0;
}

/**
 * Checks if a date is today in OFFICE timezone (Orlando)
 * Uses same business logic as isPaymentOverdue for consistency
 * @param date - The date to check
 * @returns true if the date is today in Orlando
 */
export function isDateToday(date: Date | string | null | undefined): boolean {
  const parsed = parseDateSafe(date);
  if (!parsed) return false;
  
  // Get today's date string in Orlando timezone
  const todayInOrlando = getTodayStringInOfficeTimezone(); // "2025-12-31"
  
  // Get target date as YYYY-MM-DD string
  const targetYear = parsed.getFullYear();
  const targetMonth = String(parsed.getMonth() + 1).padStart(2, '0');
  const targetDay = String(parsed.getDate()).padStart(2, '0');
  const targetString = `${targetYear}-${targetMonth}-${targetDay}`;
  
  return targetString === todayInOrlando;
}

/**
 * Checks if a date is in the past (before today) in local timezone
 * @param date - The date to check  
 * @returns true if the date is before today
 */
export function isDatePast(date: Date | string | null | undefined): boolean {
  return isPaymentOverdue(date); // Same logic
}
